import React, { FC, useState } from "react";
import { InputSwitch } from "primereact/inputswitch";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { useGetDatasetsInfo } from "api/api-hooks/datasets-hooks";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { InputNumber } from "primereact/inputnumber";
import ColorPicker from "components/basic/color-picker/ColorPicker";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { T } from "services/lang/LanguageService";
import { DialIndicatorEditorProps, LastZone, ValueType, Zone } from "./DialIndicatorTypes";
import { getHexRandomColor } from "../charts/utils";
import { valueTypeOptions } from "./DialIndicatorMinMaxValueEditor";
import "./DialIndicatorEditor.scss";

const defaultLastZone = {
  color: "red",
  highlightValue: false,
};

const DialIndicatorZonesEditor: FC<DialIndicatorEditorProps> = ({ config, onConfigChange }) => {
  const [zones, setZones] = useState<Zone[]>(config.zones ?? []);
  const [lastZone, setLastZone] = useState<LastZone[]>([
    {
      color: config.lastZone?.color ?? defaultLastZone.color,
      highlightValue: config.lastZone?.highlightValue ?? defaultLastZone.highlightValue,
    },
  ]);

  const { data } = useGetDatasetsInfo(
    zones.reduce((acc: string[], z) => {
      if (z.valueType === ValueType.DATASET && typeof z.maxValue === "string") {
        return [...acc, z.maxValue];
      }
      return acc;
    }, [])
  );
  const isDuplicateValue = (value: number, index: number) =>
    zones.some((z, i) => z.maxValue === value && i !== index);

  const onRowEditComplete = (e: DataTableRowEditCompleteEvent, isLastZone?: boolean) => {
    const { newData, index } = e;
    if (!isLastZone) {
      const oldValue = zones[index];

      if (newData.maxValue !== oldValue.maxValue && isDuplicateValue(newData.maxValue, index)) {
        return;
      }

      const updatedZones = [...zones];
      updatedZones[index] = newData as Zone;
      const sortedZones = updatedZones.sort((a, b) => {
        const aValue = typeof a.maxValue === "string" ? parseFloat(a.maxValue) : a.maxValue;
        const bValue = typeof b.maxValue === "string" ? parseFloat(b.maxValue) : b.maxValue;
        return aValue - bValue;
      });
      setZones(sortedZones);
      onConfigChange({ ...config, zones: sortedZones });
    } else {
      const updatedLastZone = [...lastZone];
      updatedLastZone[0] = newData as LastZone;
      setLastZone(updatedLastZone);
      onConfigChange({
        ...config,
        lastZone: newData as LastZone,
      });
    }
  };

  const findUniqueMaxValue = () => {
    let max = 0;
    zones.forEach(zone => {
      const maxValueAsNumber = typeof zone.maxValue === "string" ? parseFloat(zone.maxValue) : zone.maxValue;
      if (maxValueAsNumber > max) max = maxValueAsNumber;
    });
    return max + 1;
  };

  const addZone = () => {
    const newZone: Zone = {
      valueType: ValueType.CONSTANT,
      color: getHexRandomColor(),
      maxValue: findUniqueMaxValue(),
    };
    const updated = [...zones, newZone];
    setZones(updated);
    onConfigChange({ ...config, zones: updated });
  };

  const deleteZone = (currentZone: Zone) => {
    easasConfirmDialog({
      header: T("action.delete"),
      message: T("action.delete-zone"),
      confirmedCallback: () => {
        const updated = zones.filter(zone => zone.maxValue !== currentZone.maxValue);
        setZones(updated);
        onConfigChange({ ...config, zones: updated });
      },
    });
  };

  const colorEditor = (options: ColumnEditorOptions) => {
    return (
      <ColorPicker value={options.value} onValueChange={e => options.editorCallback?.(e.target.value)} />
    );
  };

  const maxValueEditor = (options: ColumnEditorOptions) => {
    const isInvalid = isDuplicateValue(options.value, options.rowIndex ?? -1);

    if (zones[options.rowIndex ?? -1]?.valueType === ValueType.DATASET) {
      return (
        <div className="flex flex-column gap-1">
          <DatasetDropdown value={options.rowData.maxValue} onChange={val => options.editorCallback?.(val)} />
          {isInvalid && <small className="p-error">{T("message.zone-value-already-exists")}</small>}
        </div>
      );
    }
    return (
      <div className="flex flex-column gap-1">
        <InputNumber
          value={options.value}
          onValueChange={e => options.editorCallback?.(e.value ?? 0)}
          invalid={isInvalid}
          className="w-10rem"
        />
        {isInvalid && <small className="p-error">{T("message.zone-value-already-exists")}</small>}
      </div>
    );
  };

  const colorBodyTemplate = (zone: LastZone) => {
    return <div className="color-body-template" style={{ backgroundColor: zone.color }} />;
  };

  const zoneDeleteButtonTemplate = (zone: Zone) => {
    return (
      <Button
        icon="pi pi-trash"
        className="dashboard-editor-button icon-red p-0"
        onClick={() => deleteZone(zone)}
      />
    );
  };

  const highlightValueBodyTemplate = (zone: Zone) => {
    return <InputSwitch checked={zone.highlightValue ?? false} disabled />;
  };

  const highlightValueEditor = (options: ColumnEditorOptions) => {
    return (
      <InputSwitch
        checked={options.rowData.highlightValue ?? false}
        onChange={e => options.editorCallback?.(e.value ?? false)}
      />
    );
  };

  const maxBodyTemplate = () => {
    return <div>{T("word.max")}</div>;
  };

  const valueTypeBodyTemplate = (zone: Zone) => {
    return <div>{zone.valueType === ValueType.DATASET ? T("word.dataset") : T("word.constant")}</div>;
  };

  const maxValueBodyTemplate = (zone: Zone) => {
    if (zone.valueType === ValueType.DATASET && typeof zone.maxValue === "string") {
      return `${data?.find(d => d.identifier === zone.maxValue)?.title?.slice(0, 30)}...`;
    }
    if ((zone.valueType === ValueType.CONSTANT || !zone.valueType) && typeof zone.maxValue === "number") {
      return zone.maxValue;
    }
    return T("error.wrong-value");
  };

  const valueTypeEditor = (options: ColumnEditorOptions) => {
    const handleValueTypeChange = (value: ValueType) => {
      options.editorCallback?.(value);
      setZones(zones.map(z => (z.maxValue === options.rowData.maxValue ? { ...z, valueType: value } : z)));
    };

    return (
      <Dropdown
        value={options.rowData.valueType ?? ValueType.CONSTANT}
        options={valueTypeOptions}
        onChange={e => handleValueTypeChange(e.value)}
      />
    );
  };

  return (
    <div className="flex flex-column gap-3">
      <Button
        label={T("action.add-zone")}
        icon="pi pi-plus"
        className="component-editor-button icon-green w-max"
        onClick={addZone}
      />
      <DataTable
        value={zones}
        editMode="row"
        dataKey="maxValue"
        onRowEditComplete={onRowEditComplete}
        size="small"
      >
        <Column
          field="color"
          header={T("label.zone-color")}
          body={colorBodyTemplate}
          editor={colorEditor}
          style={{ width: "17%" }}
        />
        <Column
          field="valueType"
          header={T("label.value-type")}
          body={valueTypeBodyTemplate}
          editor={valueTypeEditor}
          style={{ width: "16%" }}
        />
        <Column
          field="maxValue"
          header={T("label.max-value")}
          body={maxValueBodyTemplate}
          editor={maxValueEditor}
          style={{ width: "32%" }}
        />
        <Column
          field="highlightValue"
          header={T("action.highlight-value")}
          body={highlightValueBodyTemplate}
          editor={highlightValueEditor}
          style={{ width: "15%" }}
        />
        <Column
          style={{
            width: "10%",
            textAlign: "end",
          }}
          body={zoneDeleteButtonTemplate}
        />
        <Column
          rowEditor
          style={{
            width: "10%",
            textAlign: "end",
          }}
        />
      </DataTable>
      <DataTable
        editMode="row"
        onRowEditComplete={(e: DataTableRowEditCompleteEvent) => onRowEditComplete(e, true)}
        size="small"
        value={lastZone}
      >
        <Column
          field="color"
          header={T("label.last-zone-color")}
          body={colorBodyTemplate}
          editor={colorEditor}
          style={{ width: "33%" }}
        />
        <Column header={T("label.max-value")} body={maxBodyTemplate} style={{ width: "32%" }} />
        <Column
          field="highlightValue"
          header={T("action.highlight-value")}
          body={highlightValueBodyTemplate}
          editor={highlightValueEditor}
          style={{ width: "15%" }}
        />
        <Column
          style={{
            width: "10%",
          }}
        />
        <Column
          rowEditor
          style={{
            width: "10%",
            textAlign: "end",
          }}
        />
      </DataTable>
    </div>
  );
};

export default DialIndicatorZonesEditor;
